/**
 * Editor de cadencias — roadmap §F2.e.
 *
 * Una cadencia es el PLAN de comunicaciones de un proceso: qué se manda, por
 * qué canal, y con qué criterio se activa cada paso. Hasta F2.d el panel sólo
 * podía leerlas; aquí se editan.
 *
 * ## La decisión que gobierna esta pantalla: `applyScope`
 *
 * Editar una cadencia NO cambia por defecto los episodios que ya están en
 * curso. Cada episodio congela ("sella") la cadencia al abrirse —el
 * `cadence_snapshot`, patrón heredado del CRM— precisamente para que un cambio
 * de plan no altere a mitad de camino a quien ya estaba dentro.
 *
 * Por eso al guardar pasos se PIDE EXPLÍCITAMENTE el alcance, con las dos
 * opciones explicadas en castellano, y el default es el estrecho
 * (`new_only`). El daño es asimétrico: re-sellar miles de episodios en vuelo
 * no se deshace, así que el efecto ancho tiene que pedirse a conciencia. Tras
 * guardar se muestra a cuántos episodios ha afectado de verdad.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCadence,
  listCadences,
  replaceCadenceSteps,
  updateCadence,
  useMsOpsUnavailable,
} from "../../api/msops";
import { useAuth } from "../../context/AuthContext";
import {
  ActionKind,
  allCadencesSilent,
  APPLY_SCOPE_HELP,
  APPLY_SCOPE_LABEL,
  APPLY_SCOPES,
  ApplyScope,
  CADENCE_STATUS_LABEL,
  CadenceDetail,
  CadenceListItem,
  CadenceStepInput,
  CadenceWriteResponse,
  CHANNEL_LABEL,
  describeTrigger,
  msOpsErrorBody,
  MSOPS_ACTION_ERROR_HINT,
  MSOPS_MARKETS,
  MsOpsChannel,
  Paginated,
  parseTrigger,
  PROCESS_KIND_LABEL,
  StepActor,
  StepTrigger,
  TRIGGER_KIND_HELP,
  TRIGGER_KIND_LABEL,
  TRIGGER_KINDS,
  TriggerKind,
} from "../../types/msops";
import {
  Badge,
  buttonStyle,
  CadenceMarketGates,
  CadenceStatusBadge,
  CadenceStatusCell,
  COLORS,
  Card,
  describeError,
  EmptyRow,
  fieldStyle,
  fmtDateTime,
  inputStyle,
  labelStyle,
  Loading,
  secondaryButtonStyle,
  ShadowModeBanner,
  Td,
  Th,
  UnavailableNotice,
} from "./components/ui";

/** Canales que el editor ofrece. `ai_call` y `sms` están RESERVADOS en el
 * contrato (el esquema los acepta pero el ejecutor los rechaza), así que
 * ofrecerlos sería diseñar una cadencia que no se ejecuta. */
const EDITABLE_CHANNELS: MsOpsChannel[] = ["email", "whatsapp", "task", "action"];

const ACTORS: StepActor[] = ["auto", "manual"];

const ACTOR_LABEL: Record<StepActor, string> = {
  auto: "Automático",
  manual: "Manual",
};

const ACTION_KINDS: ActionKind[] = ["recreate_invoice"];

const ACTION_KIND_LABEL: Record<ActionKind, string> = {
  recreate_invoice: "Recrear factura en Stripe",
};

/**
 * Listado de cadencias. Es la pantalla de entrada del editor: `/cadencias`.
 */
export default function CadenceList() {
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [data, setData] = useState<Paginated<CadenceListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listCadences({ page: 1, pageSize: 100 }, { token }));
    } catch (err) {
      setError(describeError(err, "No se pudieron cargar las cadencias"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Aviso global: si ninguna cadencia tiene un mercado con el gate abierto, no
  // sale NADA a clientes por mucho que el listado esté lleno de filas.
  const allShadow = useMemo(
    () => allCadencesSilent(data?.items ?? []),
    [data],
  );

  if (unavailable) return <UnavailableNotice />;
  if (loading && !data) return <Loading label="Cargando cadencias…" />;

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1200, margin: "0 auto" }}>
      <Link
        to="/operations/recobros"
        style={{ color: COLORS.muted, fontSize: ".8rem", textDecoration: "none" }}
      >
        ← Recobros
      </Link>

      <h2
        style={{
          margin: "0.5rem 0 0.25rem",
          fontSize: "1.15rem",
          color: COLORS.heading,
        }}
      >
        Cadencias
      </h2>
      <p style={{ margin: "0 0 0.85rem", fontSize: ".82rem", color: COLORS.muted }}>
        El plan de comunicaciones de cada proceso. Editar una cadencia no
        cambia por defecto los episodios ya abiertos: cada uno conserva la
        versión con la que entró.
      </p>

      {allShadow && <ShadowModeBanner />}

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: COLORS.dangerBg,
            color: COLORS.dangerFg,
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <Card>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Cadencia</Th>
              <Th>Proceso</Th>
              <Th>Pasos</Th>
              <Th>Estado</Th>
              <Th>Última edición</Th>
            </tr>
          </thead>
          <tbody>
            {!data || data.items.length === 0 ? (
              <EmptyRow colSpan={5}>No hay cadencias.</EmptyRow>
            ) : (
              data.items.map((cadence) => (
                <tr
                  key={cadence.id}
                  style={{ borderTop: `1px solid ${COLORS.rowBorder}` }}
                >
                  <Td>
                    <Link
                      to={`/operations/recobros/cadencias/${cadence.id}`}
                      style={{ color: COLORS.link, fontWeight: 600 }}
                    >
                      {cadence.label}
                    </Link>
                    {!cadence.editable && (
                      <div style={{ fontSize: ".72rem", color: COLORS.muted }}>
                        Sembrada por código — sólo lectura
                      </div>
                    )}
                  </Td>
                  <Td>{PROCESS_KIND_LABEL[cadence.processKind]}</Td>
                  <Td>{cadence.stepCount}</Td>
                  <Td>
                    <CadenceStatusCell cadence={cadence} markets />
                  </Td>
                  <Td>
                    <div style={{ fontSize: ".8rem" }}>
                      {fmtDateTime(cadence.updatedAt)}
                    </div>
                    <div style={{ fontSize: ".72rem", color: COLORS.muted }}>
                      {cadence.updatedBy ?? "—"}
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/** Fila del editor: un `CadenceStepInput` con una clave estable de React. */
type DraftStep = CadenceStepInput & { key: string };

let draftKeySeq = 0;
function nextKey(): string {
  draftKeySeq += 1;
  return `draft-${draftKeySeq}`;
}

/** Trigger por defecto de un paso nuevo: el eje real de la cadencia de Ops. */
function defaultTrigger(): StepTrigger {
  return { kind: "receipts", threshold: 1 };
}

function toDraft(step: CadenceDetail["steps"][number]): DraftStep {
  return {
    key: `step-${step.id}`,
    id: step.id,
    position: step.position,
    channel: step.channel,
    actor: step.actor,
    trigger: parseTrigger(step.trigger) ?? defaultTrigger(),
    offsetMinutes: step.offsetMinutes,
    templateByMarket: { ...step.templateByMarket },
    templateVars: { ...step.templateVars },
    actionKind: step.actionKind ?? undefined,
  };
}

/**
 * Editor de UNA cadencia: metadatos arriba, pasos abajo.
 *
 * Los dos bloques se guardan por separado porque son dos endpoints distintos y
 * —más importante— porque tienen consecuencias distintas: renombrar o pausar
 * es reversible y local, mientras que reemplazar los pasos puede re-sellar
 * episodios en vuelo.
 */
export function CadenceEditor() {
  const { cadenceId } = useParams<{ cadenceId: string }>();
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [cadence, setCadence] = useState<CadenceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Metadatos -----------------------------------------------------------
  const [label, setLabel] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaError, setMetaError] = useState<ActionFailure | null>(null);
  const [metaOk, setMetaOk] = useState<string | null>(null);
  // Pausar/reanudar pasa por confirmación: es la palanca que congela el plan de
  // TODOS los episodios de esta cadencia y su efecto no es obvio desde el botón.
  const [pauseConfirm, setPauseConfirm] = useState(false);

  // --- Pasos ---------------------------------------------------------------
  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [applyScope, setApplyScope] = useState<ApplyScope>("new_only");
  const [savingSteps, setSavingSteps] = useState(false);
  const [stepsError, setStepsError] = useState<ActionFailure | null>(null);
  const [stepsResult, setStepsResult] = useState<CadenceWriteResponse | null>(
    null,
  );
  // El guardado de pasos pasa por una confirmación explícita donde se elige el
  // alcance: es la decisión irreversible de esta pantalla.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!cadenceId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await getCadence(cadenceId, { token });
      setCadence(detail);
      setLabel(detail.label);
      setSteps(detail.steps.map(toDraft));
    } catch (err) {
      setError(describeError(err, "No se pudo cargar la cadencia"));
    } finally {
      setLoading(false);
    }
  }, [cadenceId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnly = cadence ? !cadence.editable : true;

  const dirtySteps = useMemo(() => {
    if (!cadence) return false;
    return JSON.stringify(cadence.steps.map(toDraft).map(stripKey)) !==
      JSON.stringify(steps.map(stripKey));
  }, [cadence, steps]);

  const saveMeta = async (next: { label?: string; paused?: boolean }) => {
    if (!cadenceId || savingMeta) return;
    setSavingMeta(true);
    setMetaError(null);
    setMetaOk(null);
    try {
      const res = await updateCadence(cadenceId, next, { token });
      // La respuesta trae el detalle recalculado: el `status` derivado ya viene
      // actualizado, así que el badge refleja el efecto real de la acción sin
      // recargar. Se dice cuál es ese estado en el mensaje porque "reanudada" no
      // significa "enviando" mientras los gates sigan cerrados.
      setCadence(res.cadence);
      setLabel(res.cadence.label);
      setMetaOk(
        next.paused === undefined
          ? "Nombre actualizado."
          : next.paused
            ? "Cadencia pausada: no se programarán pasos nuevos."
            : `Cadencia reanudada. Estado actual: ${CADENCE_STATUS_LABEL[res.cadence.status]} — ${res.cadence.statusReason}.`,
      );
      setPauseConfirm(false);
    } catch (err) {
      setMetaError(toFailure(err, "No se pudo guardar la cadencia"));
      setPauseConfirm(false);
    } finally {
      setSavingMeta(false);
    }
  };

  const saveSteps = async () => {
    if (!cadenceId || savingSteps) return;
    setSavingSteps(true);
    setStepsError(null);
    setStepsResult(null);
    try {
      const res = await replaceCadenceSteps(
        cadenceId,
        {
          // Las posiciones se renumeran 1..N al guardar: el editor trabaja por
          // ORDEN de la lista, y dejar huecos o duplicados sólo generaría un
          // 422 que el usuario no ha provocado a conciencia.
          steps: steps.map((step, index) => ({
            ...stripKey(step),
            position: index + 1,
          })),
          applyScope,
        },
        { token },
      );
      setStepsResult(res);
      setCadence(res.cadence);
      setSteps(res.cadence.steps.map(toDraft));
      setConfirmOpen(false);
    } catch (err) {
      setStepsError(toFailure(err, "No se pudieron guardar los pasos"));
      setConfirmOpen(false);
    } finally {
      setSavingSteps(false);
    }
  };

  const updateStep = (key: string, patch: Partial<DraftStep>) => {
    setSteps((prev) =>
      prev.map((step) => (step.key === key ? { ...step, ...patch } : step)),
    );
  };

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        key: nextKey(),
        position: prev.length + 1,
        channel: "email",
        actor: "auto",
        trigger: defaultTrigger(),
        offsetMinutes: 0,
        templateByMarket: {},
        templateVars: {},
      },
    ]);
  };

  const removeStep = (key: string) => {
    setSteps((prev) => prev.filter((step) => step.key !== key));
  };

  /** Mueve un paso una posición. El orden de la lista ES la posición final. */
  const moveStep = (key: string, delta: number) => {
    setSteps((prev) => {
      const index = prev.findIndex((step) => step.key === key);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  if (unavailable) return <UnavailableNotice />;
  if (loading && !cadence) return <Loading label="Cargando cadencia…" />;

  if (!cadence) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: COLORS.dangerBg,
            color: COLORS.dangerFg,
            borderRadius: "0.45rem",
            fontSize: ".85rem",
          }}
        >
          {error ?? "Cadencia no encontrada"}
        </div>
        <Link
          to="/operations/recobros/cadencias"
          style={{ display: "inline-block", marginTop: "0.75rem", color: COLORS.link }}
        >
          ← Volver a Cadencias
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1200, margin: "0 auto" }}>
      <Link
        to="/operations/recobros/cadencias"
        style={{ color: COLORS.muted, fontSize: ".8rem", textDecoration: "none" }}
      >
        ← Cadencias
      </Link>

      <div
        style={{
          margin: "0.5rem 0 0.85rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
            {cadence.label}
          </h2>
          <div
            style={{
              marginTop: "0.35rem",
              display: "flex",
              gap: "0.35rem",
              flexWrap: "wrap",
            }}
          >
            <Badge bg="#f8f9fb" fg="#73879C">
              {PROCESS_KIND_LABEL[cadence.processKind]}
            </Badge>
            <CadenceStatusBadge
              status={cadence.status}
              reason={cadence.statusReason}
            />
            {readOnly && (
              <Badge bg="#e9ecef" fg="#495057">Sólo lectura</Badge>
            )}
          </div>
          <div
            style={{
              marginTop: "0.35rem",
              fontSize: ".8rem",
              color: COLORS.muted,
              lineHeight: 1.45,
              maxWidth: 560,
            }}
          >
            {cadence.statusReason}
          </div>
        </div>

        {/*
          Pausar se permite AUNQUE la cadencia sea `editable: false`: el back lo
          admite a propósito (`updateCadence` sólo bloquea el `label` y los
          pasos), porque apagar una cadencia sembrada por código es justo lo que
          Ops necesita poder hacer en una incidencia. Bloquearlo aquí convertiría
          la marca de protección en un obstáculo justo cuando más urge.
        */}
        <button
          type="button"
          onClick={() => setPauseConfirm(true)}
          disabled={savingMeta}
          title={
            cadence.paused
              ? "Reanudar la programación de pasos"
              : "Congela la programación de pasos nuevos (kill-switch)"
          }
          style={{
            ...buttonStyle,
            background: cadence.paused ? COLORS.link : "#856404",
            borderColor: cadence.paused ? COLORS.link : "#856404",
            opacity: savingMeta ? 0.6 : 1,
          }}
        >
          {cadence.paused ? "Reanudar" : "Pausar"}
        </button>
      </div>

      {/*
        Desglose de gates: el badge dice el estado global, esto dice mercado a
        mercado quién envía. Con rollout escalonado son datos distintos.
      */}
      <Card style={{ padding: "0.7rem 0.9rem", marginBottom: "0.65rem" }}>
        <h3 style={sectionTitle}>Gates por mercado</h3>
        <CadenceMarketGates cadence={cadence} />
        <p
          style={{
            margin: "0.5rem 0 0",
            fontSize: ".75rem",
            color: COLORS.muted,
            lineHeight: 1.45,
          }}
        >
          El gate vive en <code>app_settings</code> de ms-ops y se abre por
          mercado, no desde este panel. Mientras esté cerrado, el motor evalúa la
          cadencia y escribe la decisión en el log, pero no manda nada.
        </p>
      </Card>

      {metaOk && <Ok>{metaOk}</Ok>}
      {metaError && <Failure failure={metaError} />}

      {readOnly && (
        <Card
          style={{
            padding: "0.6rem 0.85rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
            color: COLORS.muted,
            borderLeft: `3px solid ${COLORS.border}`,
          }}
        >
          Esta cadencia está sembrada por código y no se puede editar desde el
          panel. Puedes consultarla, pero los cambios hay que hacerlos en
          ms-ops.
        </Card>
      )}

      <Card style={{ padding: "0.75rem 0.95rem", marginBottom: "0.75rem" }}>
        <h3 style={sectionTitle}>Nombre</h3>
        <div
          style={{
            display: "flex",
            gap: "0.6rem",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <label style={{ ...fieldStyle, flex: "1 1 340px" }}>
            <span style={labelStyle}>Etiqueta</span>
            <input
              type="text"
              value={label}
              maxLength={190}
              disabled={readOnly}
              onChange={(e) => setLabel(e.target.value)}
              style={inputStyle}
            />
          </label>
          <button
            type="button"
            onClick={() => saveMeta({ label: label.trim() })}
            disabled={
              savingMeta ||
              readOnly ||
              label.trim() === "" ||
              label.trim() === cadence.label
            }
            style={{
              ...buttonStyle,
              opacity:
                label.trim() === "" || label.trim() === cadence.label ? 0.6 : 1,
            }}
          >
            {savingMeta ? "Guardando…" : "Guardar nombre"}
          </button>
        </div>
      </Card>

      <Card style={{ padding: "0.75rem 0.95rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.6rem",
            flexWrap: "wrap",
            marginBottom: "0.5rem",
          }}
        >
          <h3 style={{ ...sectionTitle, margin: 0 }}>
            Pasos ({steps.length})
          </h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={addStep}
              disabled={readOnly}
              style={secondaryButtonStyle}
            >
              Añadir paso
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={readOnly || savingSteps || steps.length === 0 || !dirtySteps}
              title={
                !dirtySteps ? "No hay cambios que guardar" : "Guardar los pasos"
              }
              style={{ ...buttonStyle, opacity: dirtySteps ? 1 : 0.6 }}
            >
              Guardar pasos…
            </button>
          </div>
        </div>

        <p style={{ margin: "0 0 0.6rem", fontSize: ".78rem", color: COLORS.muted }}>
          El orden de la lista es el orden de ejecución. Al guardar se reemplaza
          el conjunto completo de pasos y se te preguntará a qué episodios debe
          afectar el cambio.
        </p>

        {stepsError && <Failure failure={stepsError} />}
        {stepsResult && <StepsSaved result={stepsResult} />}

        {steps.length === 0 ? (
          <p style={{ color: COLORS.muted, fontSize: ".85rem" }}>
            Esta cadencia no tiene pasos: no comunicaría nada.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "0.6rem" }}>
            {steps.map((step, index) => (
              <StepRow
                key={step.key}
                step={step}
                index={index}
                total={steps.length}
                readOnly={readOnly}
                onChange={(patch) => updateStep(step.key, patch)}
                onRemove={() => removeStep(step.key)}
                onMove={(delta) => moveStep(step.key, delta)}
              />
            ))}
          </div>
        )}
      </Card>

      {pauseConfirm && (
        <PauseCadenceDialog
          cadence={cadence}
          saving={savingMeta}
          onCancel={() => setPauseConfirm(false)}
          onConfirm={() => saveMeta({ paused: !cadence.paused })}
        />
      )}

      {confirmOpen && (
        <ApplyScopeDialog
          applyScope={applyScope}
          onChange={setApplyScope}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={saveSteps}
          saving={savingSteps}
        />
      )}
    </div>
  );
}

/** Editor de un paso. */
function StepRow({
  step,
  index,
  total,
  readOnly,
  onChange,
  onRemove,
  onMove,
}: {
  step: DraftStep;
  index: number;
  total: number;
  readOnly: boolean;
  onChange: (patch: Partial<DraftStep>) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  const trigger = step.trigger;
  const isAction = step.channel === "action";
  // `task` y `action` no mandan plantilla: uno es una anotación para un humano
  // y el otro ejecuta algo en otro sistema.
  const needsTemplate = step.channel === "email" || step.channel === "whatsapp";

  const setTriggerKind = (kind: TriggerKind) => {
    if (kind === trigger.kind) return;
    onChange({
      trigger:
        kind === "days"
          ? { kind: "days", offsetDays: 0 }
          : { kind, threshold: 1 },
    });
  };

  return (
    <div
      style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: "0.5rem",
        padding: "0.65rem 0.8rem",
        background: COLORS.subtle,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
          marginBottom: "0.5rem",
        }}
      >
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <Badge bg="#e7f1ff" fg="#0069d9">Paso {index + 1}</Badge>
          <span style={{ fontSize: ".78rem", color: COLORS.muted }}>
            {describeTrigger(trigger)}
          </span>
          {step.id ? (
            <span
              style={{ fontSize: ".7rem", color: COLORS.muted, fontFamily: "monospace" }}
              title="Paso existente: conserva su historial de envíos"
            >
              {step.id}
            </span>
          ) : (
            <Badge bg="#d4edda" fg="#155724">Nuevo</Badge>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={readOnly || index === 0}
            title="Subir"
            style={iconButtonStyle}
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={readOnly || index === total - 1}
            title="Bajar"
            style={iconButtonStyle}
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={readOnly}
            title="Eliminar paso"
            style={{ ...iconButtonStyle, color: COLORS.dangerFg }}
          >
            ✕
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "0.6rem",
        }}
      >
        <label style={fieldStyle}>
          <span style={labelStyle}>Canal</span>
          <select
            value={step.channel}
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                channel: e.target.value as MsOpsChannel,
                // Cambiar de canal invalida el tipo de acción: sólo los pasos
                // de canal "acción" pueden declararlo.
                actionKind:
                  e.target.value === "action" ? step.actionKind : undefined,
              })
            }
            style={inputStyle}
          >
            {EDITABLE_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Ejecución</span>
          <select
            value={step.actor}
            disabled={readOnly}
            onChange={(e) => onChange({ actor: e.target.value as StepActor })}
            style={inputStyle}
          >
            {ACTORS.map((a) => (
              <option key={a} value={a}>
                {ACTOR_LABEL[a]}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Se activa por</span>
          <select
            value={trigger.kind}
            disabled={readOnly}
            onChange={(e) => setTriggerKind(e.target.value as TriggerKind)}
            style={inputStyle}
          >
            {TRIGGER_KINDS.map((k) => (
              <option key={k} value={k}>
                {TRIGGER_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>
            {trigger.kind === "days" ? "Días laborables" : "Umbral"}
          </span>
          <input
            type="number"
            min={trigger.kind === "days" ? 0 : 1}
            value={
              trigger.kind === "days" ? trigger.offsetDays : trigger.threshold
            }
            disabled={readOnly}
            onChange={(e) => {
              const value = Number(e.target.value);
              onChange({
                trigger:
                  trigger.kind === "days"
                    ? { kind: "days", offsetDays: value }
                    : { kind: trigger.kind, threshold: value },
              });
            }}
            style={inputStyle}
          />
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Desfase intradía (min)</span>
          <input
            type="number"
            min={0}
            max={1440}
            value={step.offsetMinutes}
            disabled={readOnly}
            onChange={(e) => onChange({ offsetMinutes: Number(e.target.value) })}
            style={inputStyle}
          />
        </label>

        {isAction && (
          <label style={fieldStyle}>
            <span style={labelStyle}>Acción</span>
            <select
              value={step.actionKind ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  actionKind: (e.target.value || undefined) as
                    | ActionKind
                    | undefined,
                })
              }
              style={inputStyle}
            >
              <option value="">— Elige una acción —</option>
              {ACTION_KINDS.map((a) => (
                <option key={a} value={a}>
                  {ACTION_KIND_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div style={{ marginTop: "0.35rem", fontSize: ".73rem", color: COLORS.muted }}>
        {TRIGGER_KIND_HELP[trigger.kind]}
      </div>

      {needsTemplate && (
        <div style={{ marginTop: "0.6rem" }}>
          <div style={{ ...labelStyle, marginBottom: "0.25rem" }}>
            Plantilla por mercado
          </div>
          <div style={{ fontSize: ".73rem", color: COLORS.muted, marginBottom: "0.35rem" }}>
            Un mercado sin plantilla significa que el paso NO aplica ahí. Deja
            el campo vacío para excluirlo.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "0.4rem",
            }}
          >
            {MSOPS_MARKETS.map((market) => (
              <label key={market} style={fieldStyle}>
                <span style={labelStyle}>{market}</span>
                <input
                  type="text"
                  value={step.templateByMarket[market] ?? ""}
                  disabled={readOnly}
                  placeholder={
                    step.channel === "email"
                      ? "gs://…"
                      : "id de plantilla de Meta"
                  }
                  onChange={(e) => {
                    const next = { ...step.templateByMarket };
                    if (e.target.value.trim() === "") delete next[market];
                    else next[market] = e.target.value;
                    onChange({ templateByMarket: next });
                  }}
                  style={{ ...inputStyle, fontSize: ".76rem" }}
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Confirmación del guardado con elección EXPLÍCITA del alcance.
 *
 * No es un diálogo de "¿seguro?" decorativo: es la única pantalla donde se
 * decide si el cambio toca sólo a los episodios futuros o también a los que
 * están en curso. Por eso las dos opciones se explican con sus consecuencias
 * en vez de con sus nombres técnicos, y la ancha va marcada como irreversible.
 */
/**
 * Confirmación de pausar / reanudar.
 *
 * El texto describe lo que el back hace DE VERDAD (`updateCadence` en
 * `cadenceWrite.ts` + el veredicto `paused`/`cadencePaused` del motor):
 *
 *   - Pausar es un kill-switch que congela la PROGRAMACIÓN de pasos nuevos.
 *     Afecta a todos los episodios que se rigen por esta cadencia, incluidos
 *     los que ya están en curso — el motor los evalúa y devuelve `paused` en
 *     vez de programar el siguiente paso.
 *   - NO borra nada ni cancela lo ya programado: un `scheduled_sends` en
 *     estado `pending` sigue en pie. Prometer que "para todo" sería mentir
 *     sobre un envío que puede salir en las próximas horas.
 *   - Reanudar es simétrico y no recupera los pasos que no se programaron
 *     mientras estuvo pausada: se sigue desde donde toque ahora.
 *   - Reanudar NO implica enviar: si los gates de mercado siguen cerrados, la
 *     cadencia vuelve a `shadow`, no a `active`. Decirlo aquí evita el viaje de
 *     ida y vuelta de "le he dado a reanudar y no sale nada".
 */
function PauseCadenceDialog({
  cadence,
  saving,
  onCancel,
  onConfirm,
}: {
  cadence: CadenceDetail;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const pausing = !cadence.paused;
  // Al reanudar, el estado al que se vuelve depende de los gates, no del botón.
  const resumesInto = cadence.marketsActive.length > 0 ? "active" : "shadow";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(31,41,51,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <Card style={{ padding: "1rem 1.15rem", maxWidth: 560, width: "100%" }}>
        <h3 style={{ margin: "0 0 0.5rem", fontSize: "1rem", color: COLORS.heading }}>
          {pausing ? "¿Pausar esta cadencia?" : "¿Reanudar esta cadencia?"}
        </h3>

        {pausing ? (
          <div style={{ fontSize: ".84rem", color: COLORS.ink, lineHeight: 1.55 }}>
            <p style={{ margin: "0 0 0.5rem" }}>
              Pausar <strong>{cadence.label}</strong> congela la programación de
              pasos nuevos para todos los episodios que se rigen por ella,
              incluidos los que ya están en curso: el motor los seguirá
              evaluando, pero registrará <code>paused</code> en vez de programar
              el siguiente paso.
            </p>
            <p style={{ margin: 0, color: COLORS.dangerFg }}>
              <strong>No cancela lo ya programado:</strong> los envíos que
              estuvieran pendientes de salir siguen en pie. Si hay que detener
              uno concreto, hazlo desde su episodio.
            </p>
          </div>
        ) : (
          <div style={{ fontSize: ".84rem", color: COLORS.ink, lineHeight: 1.55 }}>
            <p style={{ margin: "0 0 0.5rem" }}>
              Reanudar <strong>{cadence.label}</strong> vuelve a programar pasos
              a partir de ahora. Los pasos que no se programaron mientras estuvo
              pausada no se recuperan: cada episodio sigue desde donde le toque
              hoy.
            </p>
            <p
              style={{
                margin: 0,
                color: resumesInto === "active" ? COLORS.dangerFg : COLORS.link,
              }}
            >
              {resumesInto === "active" ? (
                <>
                  <strong>Saldrán comunicaciones reales</strong> a clientes de{" "}
                  {cadence.marketsActive.join(", ")}: esos mercados tienen el
                  gate abierto.
                </>
              ) : (
                <>
                  Seguirá <strong>sin enviar nada</strong>: ningún mercado de
                  esta cadencia tiene el gate abierto, así que volverá a modo
                  shadow.
                </>
              )}
            </p>
          </div>
        )}

        {!cadence.editable && (
          <p
            style={{
              margin: "0.6rem 0 0",
              fontSize: ".78rem",
              color: COLORS.muted,
              lineHeight: 1.45,
            }}
          >
            Esta cadencia está sembrada por código, pero pausarla sí está
            permitido: la protección impide renombrarla y tocarle los pasos, no
            apagarla.
          </p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
            marginTop: "0.9rem",
          }}
        >
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            style={{
              ...buttonStyle,
              background: pausing ? "#856404" : COLORS.link,
              borderColor: pausing ? "#856404" : COLORS.link,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving
              ? "Guardando…"
              : pausing
                ? "Pausar cadencia"
                : "Reanudar cadencia"}
          </button>
        </div>
      </Card>
    </div>
  );
}

function ApplyScopeDialog({
  applyScope,
  onChange,
  onCancel,
  onConfirm,
  saving,
}: {
  applyScope: ApplyScope;
  onChange: (scope: ApplyScope) => void;
  onCancel: () => void;
  onConfirm: () => void;
  saving: boolean;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(31,41,51,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <Card style={{ padding: "1rem 1.15rem", maxWidth: 620, width: "100%" }}>
        <h3 style={{ margin: "0 0 0.35rem", fontSize: "1rem", color: COLORS.heading }}>
          ¿A qué episodios debe afectar este cambio?
        </h3>
        <p style={{ margin: "0 0 0.75rem", fontSize: ".82rem", color: COLORS.muted }}>
          Cada episodio guarda una copia congelada de la cadencia con la que se
          abrió. Elige si esa copia debe actualizarse también en los que ya
          están en curso.
        </p>

        <div style={{ display: "grid", gap: "0.5rem" }}>
          {APPLY_SCOPES.map((scope) => (
            <label
              key={scope}
              style={{
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
                padding: "0.6rem 0.7rem",
                borderRadius: "0.45rem",
                border: `1px solid ${
                  applyScope === scope ? COLORS.link : COLORS.border
                }`,
                background: applyScope === scope ? "#f4f9ff" : COLORS.surface,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="apply-scope"
                checked={applyScope === scope}
                onChange={() => onChange(scope)}
                style={{ marginTop: "0.15rem" }}
              />
              <span>
                <span
                  style={{
                    display: "block",
                    fontSize: ".85rem",
                    fontWeight: 700,
                    color: COLORS.ink,
                  }}
                >
                  {APPLY_SCOPE_LABEL[scope]}
                  {scope === "waiting_too" && (
                    <span style={{ marginLeft: "0.4rem" }}>
                      <Badge bg="#fff3cd" fg="#856404">Irreversible</Badge>
                    </span>
                  )}
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: "0.2rem",
                    fontSize: ".78rem",
                    color: COLORS.muted,
                    lineHeight: 1.45,
                  }}
                >
                  {APPLY_SCOPE_HELP[scope]}
                </span>
              </span>
            </label>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
            marginTop: "0.85rem",
          }}
        >
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            style={buttonStyle}
          >
            {saving ? "Guardando…" : "Guardar pasos"}
          </button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Resultado del guardado. `resyncedEpisodes` es el dato que confirma qué se
 * acaba de hacer: con `new_only` es siempre 0, y verlo explícitamente es la
 * prueba de que NO se tocó nada en vuelo.
 */
function StepsSaved({ result }: { result: CadenceWriteResponse }) {
  const wide = result.applyScope === "waiting_too";
  return (
    <div
      style={{
        padding: "0.6rem 0.75rem",
        borderRadius: "0.45rem",
        background: "#d4edda",
        color: "#155724",
        fontSize: ".82rem",
        marginBottom: "0.6rem",
      }}
    >
      <strong>Pasos guardados.</strong>{" "}
      {wide
        ? result.resyncedEpisodes === 0
          ? "No había ningún episodio abierto que resincronizar."
          : `Se han resincronizado ${result.resyncedEpisodes} episodio(s) en curso: a partir de ahora se rigen por esta versión.`
        : "Los episodios ya abiertos siguen con su plan anterior; sólo los nuevos usarán esta versión."}
    </div>
  );
}

/** Fallo de una acción, con el motivo del back y la pista de qué hacer. */
type ActionFailure = {
  message: string;
  hint: string | null;
  /** Muestra de episodios en vuelo que bloquean el borrado (409). */
  episodes: string[];
  episodeCount: number | null;
};

function toFailure(err: unknown, fallback: string): ActionFailure {
  const body = msOpsErrorBody(err);
  const status = (err as { status?: number } | null)?.status;
  if (!body) {
    return {
      message:
        typeof status === "number" ? `${fallback} (HTTP ${status})` : fallback,
      hint: null,
      episodes: [],
      episodeCount: null,
    };
  }
  const detail = body.detail ?? {};
  const rawEpisodes = detail.episodes;
  const episodes = Array.isArray(rawEpisodes)
    ? rawEpisodes.filter((e): e is string => typeof e === "string")
    : [];
  const count = detail.episodeCount;
  return {
    message: body.message || fallback,
    hint: MSOPS_ACTION_ERROR_HINT[body.error] ?? null,
    episodes,
    episodeCount: typeof count === "number" ? count : null,
  };
}

function Failure({ failure }: { failure: ActionFailure }) {
  return (
    <div
      style={{
        padding: "0.55rem 0.75rem",
        borderRadius: "0.45rem",
        background: COLORS.dangerBg,
        color: COLORS.dangerFg,
        fontSize: ".8rem",
        marginTop: "0.5rem",
      }}
    >
      <div>{failure.message}</div>
      {failure.hint && (
        <div style={{ marginTop: "0.25rem" }}>{failure.hint}</div>
      )}
      {failure.episodes.length > 0 && (
        <div style={{ marginTop: "0.35rem" }}>
          <div style={{ fontWeight: 600 }}>
            Episodios afectados
            {failure.episodeCount !== null ? ` (${failure.episodeCount})` : ""}:
          </div>
          <ul style={{ margin: "0.2rem 0 0", paddingLeft: "1.1rem" }}>
            {failure.episodes.map((id) => (
              <li key={id} style={{ fontFamily: "monospace", fontSize: ".75rem" }}>
                <Link
                  to={`/operations/recobros/${id}`}
                  style={{ color: COLORS.dangerFg }}
                >
                  {id}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Ok({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "0.5rem 0.7rem",
        borderRadius: "0.45rem",
        background: "#d4edda",
        color: "#155724",
        fontSize: ".8rem",
        marginTop: "0.5rem",
      }}
    >
      {children}
    </div>
  );
}

/** Quita la clave de React antes de mandar el paso al back. */
function stripKey(step: DraftStep): CadenceStepInput {
  const { key, ...rest } = step;
  return rest;
}

const sectionTitle: React.CSSProperties = {
  margin: "0 0 0.5rem",
  fontSize: ".9rem",
  color: COLORS.heading,
};

const iconButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  padding: "0.2rem 0.45rem",
  fontSize: ".85rem",
  lineHeight: 1,
};
